import { IPermissionCatalogItem } from "../../../@types/admin/AdminAccess";
import { adminMenuGroups } from "../../../routes/admin.routes";

/**
 * Grouping for the permission checklist (EN-5129). The catalog is one flat
 * list and grows by one entry per migrated Retool page — 32 by the end of the
 * EN-5146 epic — which is unreadable as a single column.
 *
 * The groups are derived from `adminMenuGroups` rather than hard-coded here:
 * every page permission is already attached to its menu entry as
 * `requiredPermission`, so a page grouped under "Patients" in the sidebar is
 * grouped under "Patients" here, and a migration ticket that adds its menu
 * entry gets the grouping for free. Nothing to keep in sync.
 *
 * ⚠️ Built from `adminMenuGroups`, NOT `getVisibleAdminMenuGroups()` — this
 * is a management screen and has to list every permission, including the ones
 * the operator using it doesn't hold themselves.
 */

/** Permissions that aren't a page's own — currently the two `:read` feeds. */
const UNGROUPED_LABEL = "Other";

export interface IPermissionGroup {
  name: string;
  permissions: IPermissionCatalogItem[];
}

/** Menu group name for each permission code the sidebar knows about. */
function buildMenuGroupIndex(): Map<string, string> {
  const index = new Map<string, string>();

  adminMenuGroups.forEach((group) => {
    // A group is either one entry with its own permission (Staff) or a
    // parent with children (Patients, Organisation, …).
    if (group.requiredPermission) {
      index.set(group.requiredPermission, group.name);
    }
    group.children?.forEach((child) => {
      if (child.requiredPermission) {
        index.set(child.requiredPermission, group.name);
      }
    });
  });

  return index;
}

/**
 * The catalog split into sidebar-shaped groups, each group's permissions in
 * catalog order. Groups appear in sidebar order; anything the sidebar doesn't
 * claim lands in a trailing "Other" group. Empty groups are dropped.
 */
export function groupPermissions(
  catalog: IPermissionCatalogItem[]
): IPermissionGroup[] {
  const menuGroupByCode = buildMenuGroupIndex();

  // Seeded in sidebar order so the sections read in the same order as the
  // menu, then filtered to the ones that actually have permissions.
  const groups = new Map<string, IPermissionCatalogItem[]>();
  adminMenuGroups.forEach((group) => groups.set(group.name, []));
  groups.set(UNGROUPED_LABEL, []);

  catalog.forEach((permission) => {
    const groupName = menuGroupByCode.get(permission.code) ?? UNGROUPED_LABEL;
    const bucket = groups.get(groupName);
    if (bucket) {
      bucket.push(permission);
    } else {
      groups.set(groupName, [permission]);
    }
  });

  return Array.from(groups.entries())
    .filter(([, permissions]) => permissions.length > 0)
    .map(([name, permissions]) => ({ name, permissions }));
}

/**
 * Every page permission's display name is the same sentence with the page
 * name swapped in ("Access the Billable Devices admin page"), which sits
 * directly under a code that already says it, inside a group heading that
 * says it a third time. Those are dropped so a page permission is one line.
 *
 * Anything phrased differently — `admin-reports:read` ("Read admin reports"),
 * `audit-logs:read` — keeps its description, because there the wording is the
 * only thing explaining what the permission covers. If the API ever rewords
 * the boilerplate this just stops matching and the descriptions come back,
 * which is the safe direction to fail.
 */
const PAGE_ACCESS_BOILERPLATE = /^access the .+ admin pages?$/i;

export function isRedundantDisplayName(
  permission: IPermissionCatalogItem
): boolean {
  return PAGE_ACCESS_BOILERPLATE.test(permission.displayName.trim());
}

/** Case-insensitive match on the code or the display name. */
export function matchesPermissionSearch(
  permission: IPermissionCatalogItem,
  searchTerm: string
): boolean {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return true;
  return (
    permission.code.toLowerCase().includes(term) ||
    permission.displayName.toLowerCase().includes(term)
  );
}

/** Groups with their permissions filtered; groups left empty are dropped. */
export function filterPermissionGroups(
  groups: IPermissionGroup[],
  searchTerm: string
): IPermissionGroup[] {
  if (!searchTerm.trim()) return groups;

  return groups
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter((permission) =>
        matchesPermissionSearch(permission, searchTerm)
      ),
    }))
    .filter((group) => group.permissions.length > 0);
}
